import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { scrollToId } from "@/components/Shared";

const TICKS = Array.from({ length: 90 });

export default function RulerStrip({ products }) {
  const trackRef = useRef(null);
  const [index, setIndex] = useState(1);
  const total = products.length || 1;

  const onScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    const thumb = 176;
    setIndex(Math.min(total, Math.max(1, Math.round(el.scrollLeft / thumb) + 1)));
  };

  const nudge = (dir) => {
    trackRef.current?.scrollBy({ left: dir * 360, behavior: "smooth" });
  };

  if (!products.length) return null;

  return (
    <div className="px-5 sm:px-10 pt-6 pb-20 border-b border-white/10">
      <div className="flex items-end justify-between mb-6">
        <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">Lookbook Index</p>
        <div className="flex items-center gap-5">
          <span data-testid="lookbook-counter" className="font-unbounded text-xs text-white/60">
            {String(index).padStart(2, "0")} <span className="text-white/30">//</span> {String(total).padStart(2, "0")}
          </span>
          <div className="flex gap-2">
            <button data-testid="lookbook-prev" onClick={() => nudge(-1)} aria-label="Previous looks" className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-white/60 transition-colors duration-300 hover:bg-white hover:text-black">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button data-testid="lookbook-next" onClick={() => nudge(1)} aria-label="Next looks" className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-white/60 transition-colors duration-300 hover:bg-white hover:text-black">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex items-end gap-[5px] h-5 mb-3 overflow-hidden" aria-hidden="true">
        {TICKS.map((_, i) => (
          <span key={i} className={`w-px shrink-0 bg-white/25 ${i % 10 === 0 ? "h-5 bg-white/50" : i % 5 === 0 ? "h-3.5" : "h-2"}`} />
        ))}
      </div>

      <div
        ref={trackRef}
        onScroll={onScroll}
        data-testid="lookbook-strip"
        className="flex gap-4 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {products.map((p, i) => (
          <button
            key={p.lookup_key}
            data-testid={`lookbook-thumb-${p.lookup_key}`}
            onClick={() => scrollToId(p.category === "varsity" ? "varsity" : "collection")}
            className="group shrink-0 text-left"
          >
            <img
              src={p.image}
              alt={p.name}
              loading="lazy"
              className="w-40 h-52 object-cover rounded-sm grayscale group-hover:grayscale-0 group-hover:scale-[1.03] transition-all duration-700"
            />
            <p className="mt-2.5 text-[9px] uppercase tracking-[0.2em] text-white/40 group-hover:text-white transition-colors duration-300">
              {String(i + 1).padStart(2, "0")} — {p.name}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}
